import './styles/checkout.scss';
import './styles/general.scss';
import './styles/modal.scss';
import './styles/order-bump.scss';
import './styles/plans.scss';
import './styles/plugins.scss';
import api from 'api';
import BuyButton from 'BuyButton';
import { useCallback } from 'react';
import {
  pixelNative,
  pixelTikTok,
  googleAnalyticsSend,
  injectGoogleTagManager,
  googleAdsSend,
} from 'functions.js';
import Loader from 'Loader';
import MethodCard from 'MethodCard';
import ModalBillet from 'ModalBillet';
import ModalCard from 'ModalCard';
import ModalPix from 'ModalPix';
import OrderBump from 'OrderBump';
import Pixels from 'Pixels';
import Plans from 'Plans';
import { createRef, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import Summary from 'Summary';
import SvgPix from 'SvgPix';
import MethodBillet from './MethodBillet';
import MethodPix from './MethodPix';
import useQuery from 'query/queryHook';
import CheckoutUserData from 'CheckoutUserData';
import Counter from 'plugins/Counter';
import Notifications from 'plugins/Notifications';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const useQueryParms = () => {
  const { search } = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(search);
    const result = {};
    for (const [key, value] of params.entries()) {
      result[key] = value;
    }
    return result;
  }, [search]);
};

function lightOrDark(color) {
  let r, g, b;
  if (color.match(/^rgb/)) {
    color = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );
    r = color[1];
    g = color[2];
    b = color[3];
  } else {
    color = +('0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));
    r = color >> 16;
    g = (color >> 8) & 255;
    b = color & 255;
  }
  let hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  if (hsp > 127.5) {
    return 'light';
  } else {
    return 'dark';
  }
}

const Checkout = ({ pixels, setPixels }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [allowedBillet, setAllowedBillet] = useState(false);
  const [allowedCard, setAllowedCard] = useState(false);
  const [allowedPix, setAllowedPix] = useState(false);
  const [customerDataSent, setCustomerDataSent] = useState(false);
  const [discounts, setDiscounts] = useState(null);
  const [fullName, setFullName] = useState('');
  const [googlePixels, setGooglePixels] = useState([]);
  const [googleAdsPixels, setGoogleAdsPixels] = useState([]);
  const [installmentsList, setInstallmentsList] = useState([]);
  const [offer, setOffer] = useState(null);
  const [orderBumps, setOrderBumps] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [plans, setPlans] = useState([]);
  const [processedBy, setProcessedBy] = useState(null);
  const [product, setProduct] = useState(null);
  const [selectedBumps, setSelectedBumps] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [installmentsHaveInterest, setInstallmentsHaveInterest] =
    useState(null);
  const [fbPixels, setFbPixels] = useState([]);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [counter, setCounter] = useState(null);
  const query = useQuery();

  const [coupon, setCoupon] = useState(null);

  const { a: affiliateUuid = null, split: splitParam } = useQueryParms();

  const { uuidOffer } = useParams();

  const [errorMessage, setErrorMessage] = useState(null);

  const {
    register,
    formState,
    setValue,
    getValues,
    control,
    setFocus,
    setError,
    clearErrors,
    handleSubmit,
  } = useForm({
    mode: 'onSubmit',
  });
  const { errors } = formState;

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('sale_card');
    setRecaptchaToken(token);
    // Do whatever you want with the token
  }, [executeRecaptcha]);

  // You can use useEffect to trigger the verification as soon as the component being loaded
  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const onSubmit = async (data, e) => {
    e.preventDefault();
    await handleReCaptchaVerify();
    setShowModal(true);
  };
  const onError = async (errors, e) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (pixels) {
      const pixelsConfig = [];
      if (pixels.facebook.length > 0) {
        pixels.facebook.forEach((item) => {
          const test_event_code = query.get('test_event_code');
          pixelsConfig.push({
            target: 'iframe', // iframe send events
            pixel_id: item.settings.pixel_id,
            paid_pix: item.settings.paid_pix,
            generated_pix: item.settings.generated_pix,
            pixel_uuid: item.uuid,
            event_id: pixels.sessionPixelsEventId,
            test_event_code,
            url: `https://${item.settings.domain}/pixel.html?pixel=${item.settings.pixel_id}&event_id=${pixels.sessionPixelsEventId}&pixel_uuid=${item.uuid}`,
            ref: createRef(),
          });
          window.fbq('init', `${item.settings.pixel_id}`);
        });

        setFbPixels(pixelsConfig);
      }
      if (pixels.tiktok.length > 0) {
        pixels.tiktok.forEach((item) => {
          window.ttq.load(item.settings.pixel_id);
        });
        window.ttq.page();
      }

      if (pixels.facebook.length > 0) {
        pixelNative('InitiateCheckout', {
          value: offer.price,
          currency: 'BRL',
          content_ids: [offer.uuid],
          num_items: 1,
        });
      }

      if (pixels.tiktok.length > 0) {
        pixelTikTok('InitiateCheckout');
      }

      if (pixels['google-analytics'].length > 0) {
        setGooglePixels(pixels['google-analytics']);
        pixels['google-analytics'].forEach((item, index) => {
          injectGoogleTagManager(item.settings.pixel_id);
          googleAnalyticsSend('begin_checkout', {
            currency: 'BRL',
            value: offer.price,
            items: [
              {
                item_name: offer.product.name,
                index,
                quantity: 1,
                price: offer.price,
              },
            ],
          });
        });
      }

      if (pixels['google-ads'].length > 0) {
        setGoogleAdsPixels(pixels['google-ads']);
        pixels['google-ads'].forEach((item) => {
          injectGoogleTagManager(item.settings.pixel_id);
          if (item.settings.initiate_checkout)
            googleAdsSend({
              send_to: `${item.settings.pixel_id}/${item.settings.purchase_label}`,
            });
        });
      }
    }
  }, [pixels]);

  useEffect(async () => {
    try {
      if (affiliateUuid) {
        await api.get(`/offers/${uuidOffer}/${affiliateUuid}`);
      }

      const response = await api.get(`/offers/${uuidOffer}`);

      setCounter(response.data.counter);
      setProduct(response.data.product);
      setOffer(response.data);
      setPlans(response.data.payment.plans);
      setOrderBumps(
        response.data.order_bumps.map((ob) => ({ ...ob, quantity: 0 }))
      );
      setTotalPrice(response.data.price);
      setPixels(response.data.pixels);
      setProcessedBy(response.data.proccessed_by);
      setInstallmentsList(response.data.payment.installments_list);

      if (response.data.payment.methods.includes('credit_card')) {
        setValue(
          'installments',
          response.data.payment.installments_list.length
        );
      }

      if (
        splitParam &&
        response.data.payment.installments_list.find(
          (item) => item.n === +splitParam
        )
      ) {
        setValue('installments', +splitParam);
      }

      setDiscounts(response.data.discounts);
      setInstallmentsHaveInterest(
        response.data.payment.installments_have_insterest
      );

      const selectedMethod = response.data.payment.methods[0];
      setPaymentMethod(
        selectedMethod === 'credit_card' ? 'card' : selectedMethod
      );

      if (response.data.payment.plans) {
        setSelectedPlan(response.data.payment.plans[0]);
      }

      if (response.data.payment.methods.includes('pix')) {
        setAllowedPix(true);
      }
      if (response.data.payment.methods.includes('billet')) {
        setAllowedBillet(true);
      }
      if (response.data.payment.methods.includes('credit_card')) {
        setAllowedCard(true);
      }

      document.title = response.data.product.name;
      if (response.data.checkout.favicon) {
        document.getElementById('favicon').href =
          response.data.checkout.favicon;
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message);
    }
  }, []);

  useEffect(() => {
    if (offer) {
      const theme = lightOrDark(offer.checkout.hex_color);
      if (offer.checkout.hex_color) {
        if (theme === 'light') {
          document.documentElement.style.setProperty(
            '--primary-color',
            '#000000'
          );
        } else {
          document.documentElement.style.setProperty(
            '--primary-color',
            offer.checkout.hex_color
          );
        }
        document.documentElement.style.setProperty(
          '--background-color',
          offer.checkout.hex_color
        );
      }
    }
  }, [offer]);

  useEffect(() => {
    if (selectedBumps && offer) {
      let subTotal = offer.price;
      selectedBumps.forEach((item) => {
        subTotal += item.price;
      });
      setTotalPrice(subTotal);

      let newList = offer.payment.installments_list.map((item) => ({
        ...item,
      }));

      const bumps = selectedBumps.filter((item) => item.quantity);

      for (let bump of bumps) {
        for (let [index, installment] of bump.installments_list.entries()) {
          newList[index].price += installment.price;
          newList[index].total += installment.total;
        }
      }

      setInstallmentsList(newList);
    }
  }, [selectedBumps]);

  useEffect(() => {
    if (paymentMethod === 'card') {
      setValue('installments', '1');
    }
  }, [paymentMethod]);

  const updateQuantity = (index, value) => {
    setOrderBumps((prevOb) => {
      prevOb[index].quantity += value;
      return [...prevOb];
    });
  };

  useEffect(() => {
    if (paymentMethod !== 'card') {
      clearErrors('number');
      clearErrors('expiry');
      clearErrors('cvc');
      clearErrors('cardHolder');
    }
  }, [paymentMethod]);

  return (
    <>
      {counter && <Counter counterObj={counter} />}
      {query.get('notifications') && <Notifications />}
      <Modal
        id='modal'
        show={showModal}
        size='lg'
        onHide={() => setShowModal(false)}
        backdrop='static'
        keyboard={false}
      >
        {paymentMethod === 'billet' && (
          <ModalBillet
            uuidOffer={uuidOffer}
            orderBumps={orderBumps}
            totalPrice={totalPrice}
            selectedBumps={selectedBumps}
            pixels={pixels}
            fbPixels={fbPixels}
            googlePixels={googlePixels}
            googleAds={googleAdsPixels}
            offer={offer}
            coupon={coupon}
          />
        )}
        {paymentMethod === 'pix' && (
          <ModalPix
            paymentStatus={paymentStatus}
            uuidOffer={uuidOffer}
            offer={offer}
            setPaymentStatus={setPaymentStatus}
            orderBumps={orderBumps}
            setShowModal={setShowModal}
            product={product}
            selectedBumps={selectedBumps}
            totalPrice={totalPrice}
            selectedPlan={selectedPlan}
            pixels={fbPixels}
            googlePixels={googlePixels}
            googleAds={googleAdsPixels}
            orderBumpsFinal={orderBumps}
            paymentMethod={paymentMethod}
            coupon={coupon}
          />
        )}
        {paymentMethod === 'card' && (
          <ModalCard
            offer={offer}
            uuidOffer={uuidOffer}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            getValues={getValues}
            totalPrice={totalPrice}
            selectedPlan={selectedPlan}
            plans={plans}
            selectedBumps={selectedBumps}
            installmentsHaveInterest={installmentsHaveInterest}
            fullName={fullName}
            pixels={fbPixels}
            googlePixels={googlePixels}
            googleAds={googleAdsPixels}
            recaptchaToken={recaptchaToken}
            coupon={coupon}
          />
        )}
      </Modal>

      {!offer && errorMessage && (
        <Loader title={errorMessage} spinner={false} />
      )}
      {!offer && !errorMessage && <Loader />}
      {offer && (
        <>
          <Pixels pixelList={offer.pixels} />
          <div className='container'>
            <Row className='row-content'>
              <Col lg={12}>
                {offer.checkout.header_picture && (
                  <img src={offer.checkout.header_picture} className='header' />
                )}
                {offer.checkout.header_picture_mobile ? (
                  <img
                    src={offer.checkout.header_picture_mobile}
                    className='header-m'
                  />
                ) : (
                  <img
                    src={offer.checkout.header_picture}
                    className='header-m'
                  />
                )}
                {offer.checkout.second_header_mobile && (
                  <img
                    src={offer.checkout.second_header_mobile}
                    className='header-m'
                  />
                )}
              </Col>
              <Col lg={9}>
                <form onSubmit={handleSubmit(onSubmit, onError)}>
                  <div className='header input-group mb-3 '>
                    {product.logo ? (
                      <img
                        src={product.logo}
                        className='logo'
                        alt={offer.offer.name + ' - logo'}
                      />
                    ) : (
                      <h2 className='title'>{offer.offer.name}</h2>
                    )}
                  </div>
                  <CheckoutUserData
                    orderBumps={orderBumps}
                    offer={offer}
                    setCustomerDataSent={setCustomerDataSent}
                    fbPixels={fbPixels}
                    googlePixels={googlePixels}
                    setFullName={setFullName}
                    uuidOffer={uuidOffer}
                    product={product}
                    register={register}
                    formState={formState}
                    setValue={setValue}
                    getValues={getValues}
                    control={control}
                    setFocus={setFocus}
                    setError={setError}
                    clearErrors={clearErrors}
                  />

                  <div className='pay-info form-group'>
                    {allowedCard && (
                      <div className='input-group-2'>
                        <div className='area'>
                          <button
                            type='button'
                            id='btn-card'
                            className={
                              paymentMethod === 'card' ? 'btn active' : 'btn'
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              setPaymentMethod('card');
                            }}
                          >
                            <i className='las la-credit-card' />
                            <span>Cartão de crédito</span>

                            {discounts && discounts.card > 0 && (
                              <span className='discount-label'>{` -${discounts.card}%`}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {allowedPix && (
                      <div className='input-group-2'>
                        <div className='area'>
                          <button
                            type='button'
                            id='btn-credit-billet'
                            className={
                              paymentMethod === 'pix'
                                ? 'btn btn-pix active'
                                : 'btn btn-pix'
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              setPaymentMethod('pix');
                            }}
                          >
                            <SvgPix />
                            <span>Pix</span>
                            {discounts && discounts.pix > 0 && (
                              <span className='discount-label'>{` -${discounts.pix}%`}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {allowedBillet && (
                      <div className='input-group-2'>
                        <div className='area'>
                          <button
                            type='button'
                            id='btn-credit-billet'
                            className={
                              paymentMethod === 'billet' ? 'btn active' : 'btn'
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              setPaymentMethod('billet');
                            }}
                          >
                            <i className='las la-file-alt' />
                            <span>Boleto</span>
                            {discounts && discounts.billet > 0 && (
                              <span className='discount-label'>{` -${discounts.billet}%`}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {paymentMethod == 'card' && (
                    <div className='pay-card'>
                      <MethodCard
                        register={register}
                        errors={errors}
                        offer={offer}
                        installmentsList={installmentsList}
                        paymentMethod={paymentMethod}
                        getValues={getValues}
                      />
                    </div>
                  )}
                  {paymentMethod == 'billet' && (
                    <div className='pay-billet'>
                      <MethodBillet
                        offer={offer}
                        selectedBumps={selectedBumps}
                      />
                    </div>
                  )}
                  {paymentMethod == 'pix' && (
                    <div className='pay-pix'>
                      <MethodPix offer={offer} selectedBumps={selectedBumps} />
                    </div>
                  )}
                  {orderBumps.length > 0 && (
                    <OrderBump
                      offer={offer}
                      products={orderBumps}
                      updateQuantity={updateQuantity}
                      selectedBumps={selectedBumps}
                      setSelectedBumps={setSelectedBumps}
                    />
                  )}
                  {plans.length > 0 && (
                    <Plans
                      checkout={offer.checkout}
                      plans={plans}
                      selectedPlan={selectedPlan}
                      setSelectedPlan={setSelectedPlan}
                    />
                  )}
                  <Summary
                    offer={offer}
                    product={product}
                    selectedBumps={selectedBumps}
                    orderBumps={orderBumps}
                    discounts={discounts}
                    paymentMethod={paymentMethod}
                    totalPrice={totalPrice}
                    selectedPlan={selectedPlan}
                    plans={plans}
                    coupon={coupon}
                    setCoupon={setCoupon}
                  />
                  <BuyButton customerDataSent={customerDataSent} />
                  {product && (
                    <div className='footer'>
                      <div>
                        {offer.payment.student_pays_interest
                          ? '* Parcelamento com tarifa de 2.99% a.m.'
                          : 'Parcelamento sem juros.'}
                      </div>
                      <div>
                        <span>Precisa de ajuda?</span> Contato do(a)
                        vendedor(a):{' '}
                        <div style={{ margin: 0 }}>
                          <a href={`mailto:${product.support_email}`}>
                            {product.support_email}
                          </a>{' '}
                          {product.support_whatsapp && (
                            <>
                              ou{' '}
                              <a
                                href={`https://wa.me/+55${product.support_whatsapp
                                  .replace(/\s/g, '')
                                  .replaceAll('_', '')}`}
                                target='_blank'
                                rel='noreferrer'
                              >
                                {product.support_whatsapp.replaceAll('_', '')}
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      {processedBy && (
                        <div>
                          <b>B4you</b> está processando esta venda, ao
                          continuar, você concorda com os{' '}
                          <a href='#'>Termos de Compra</a>. Todos os direitos
                          reservados. Referência de oferta:
                          {' ' + uuidOffer}
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </Col>
              {offer.checkout.sidebar_picture && (
                <Col lg={3}>
                  <img
                    src={offer.checkout.sidebar_picture}
                    className='sidebar'
                  />
                </Col>
              )}
            </Row>
          </div>
        </>
      )}
    </>
  );
};

export default Checkout;
